import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { SharedModule } from '../shared/shared.module';
import { addIcons } from 'ionicons';
import {
  menuOutline,
  close,
  chevronDownOutline,
  saveOutline,
  homeOutline,
  timeOutline,
} from 'ionicons/icons';
import { TranslationService, LanguageItem } from '../shared/services/translation.service';
import { parseQueryParams } from '../shared/utils/parse-query-params';
import { filter, lastValueFrom } from 'rxjs';
import { ApplicationService, AuthService } from '../shared/services';
import { CncsWizardService } from '../pages/wizards/cncs-wizard/cncs-wizard/cncs-wizard.service';
import { SsdgWizardService } from '../pages/wizards/ssdg-wizard/ssdg-wizard.service';
import { MsdgWizardService } from '../pages/wizards/msdg-wizard/msdg-wizard.service';
import { AgrivoltaicOneWizardService } from '../pages/wizards/agrivoltaic-one-wizard/agrivoltaic-one-wizard.service';
import { AgrivoltaicTwoWizardService } from '../pages/wizards/agrivoltaic-two-wizard/agrivoltaic-two-wizard.service';
import { SimulationWizardService } from '../pages/simulation/simulation-wizard.service';
import { ImpersonationBannerComponent } from '../components/impersonation-banner/impersonation-banner.component';
import { WizardBrandingService } from '../pages/wizards/wizard-branding.service';

@Component({
  selector: 'app-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [SharedModule, ImpersonationBannerComponent],
})
export class HeaderComponent implements OnInit, AfterViewInit {
  appName = '';
  isMenuOpen = false;
  isSimulationPage = false;
  languages: LanguageItem[] = [];
  languageSelectPopoverOptions = { cssClass: 'language-select-popover' };
  readonly activeWizardBranding = this.wizardBrandingService.activeBranding;
  readonly headerSubtitleKey = computed(() => {
    const branding = this.activeWizardBranding();
    switch (branding.key) {
      case 'cncs':
        return 'application.wizard.cncs.header.title';
      case 'ssdg':
        return 'application.wizard.ssdg.header.title';
      case 'msdg':
        return 'application.wizard.msdg.header.title';
      case 'agrivoltaic-one':
        return 'application.wizard.agrivoltaic_one.header.title';
      case 'agrivoltaic-two':
        return 'application.wizard.agrivoltaic_two.header.title';
      default:
        return '';
    }
  });
  /** Status (clock) button: only when user is manager and current application status is completed. */
  readonly showManagerStatusButton = computed(
    () =>
      this.authService.isManager() &&
      (
        this.cncsWizardService.applicationStatusIsCompleted() ||
        this.ssdgWizardService.applicationStatusIsCompleted() ||
        this.msdgWizardService.applicationStatusIsCompleted() ||
        this.agrivoltaicOneWizardService.applicationStatusIsCompleted() ||
        this.agrivoltaicTwoWizardService.applicationStatusIsCompleted()
      )
  );

  /** Save button visible when either application or simulation wizard is active and editable. */
  readonly showSaveButton = computed(
    () =>
      this.cncsWizardService.showSaveInHeader() ||
      this.ssdgWizardService.showSaveInHeader() ||
      this.msdgWizardService.showSaveInHeader() ||
        this.agrivoltaicOneWizardService.showSaveInHeader() ||
        this.agrivoltaicTwoWizardService.showSaveInHeader() ||
      this.simulationWizardService.showSaveInHeader()
  );
  /** Red dot when either wizard has unsaved changes. */
  readonly showUnsavedIndicator = computed(
    () =>
      this.cncsWizardService.showUnsavedIndicator() ||
      this.ssdgWizardService.showUnsavedIndicator() ||
      this.msdgWizardService.showUnsavedIndicator() ||
        this.agrivoltaicOneWizardService.showUnsavedIndicator() ||
        this.agrivoltaicTwoWizardService.showUnsavedIndicator() ||
      this.simulationWizardService.showUnsavedIndicator()
  );

  /** Tooltip and aria-label for the save button (application vs simulation). */
  readonly saveButtonTooltip = computed(() => {
    if (this.simulationWizardService.showSaveInHeader()) {
      return this.translation.translate.instant('application.simulation.save') || 'Save simulation';
    }
    return this.translation.translate.instant('application.wizard.application.save') || 'Save';
  });

  private readonly defaultTopBarHeightPx = 56;

  constructor(
    private destroyRef: DestroyRef,
    private elRef: ElementRef<HTMLElement>,
    private router: Router,
    private location: Location,
    private applicationService: ApplicationService,
    private authService: AuthService,
    public translation: TranslationService,
    private cncsWizardService: CncsWizardService,
    private ssdgWizardService: SsdgWizardService,
    private msdgWizardService: MsdgWizardService,
    private agrivoltaicOneWizardService: AgrivoltaicOneWizardService,
    private agrivoltaicTwoWizardService: AgrivoltaicTwoWizardService,
    private simulationWizardService: SimulationWizardService,
    private wizardBrandingService: WizardBrandingService,
  ) {
    addIcons({ menuOutline, close, chevronDownOutline, saveOutline, homeOutline, timeOutline });
  }

  onSaveApplication(): void {
    if (this.cncsWizardService.showSaveInHeader()) {
      this.cncsWizardService.requestSave();
    } else if (this.ssdgWizardService.showSaveInHeader()) {
      this.ssdgWizardService.requestSave();
    } else if (this.msdgWizardService.showSaveInHeader()) {
      this.msdgWizardService.requestSave();
    } else if (this.agrivoltaicOneWizardService.showSaveInHeader()) {
      this.agrivoltaicOneWizardService.requestSave();
    } else if (this.agrivoltaicTwoWizardService.showSaveInHeader()) {
      this.agrivoltaicTwoWizardService.requestSave();
    } else if (this.simulationWizardService.showSaveInHeader()) {
      this.simulationWizardService.requestSave();
    }
  }

  get currentLang(): string {
    const lang = this.translation.translate.currentLang ?? this.translation.translate.getCurrentLang();
    return (lang || 'en').toUpperCase().slice(0, 2);
  }

  onLanguageChange(lang: string): void {
    this.translation.setTranslationTo(lang);
  }

  getFlagEmoji(countryCode: string): string {
    if (!countryCode || countryCode.length !== 2) {
      return '🌐';
    }
    return String.fromCodePoint(
      ...countryCode
        .toUpperCase()
        .split('')
        .map((char) => 127397 + char.charCodeAt(0))
    );
  }

  async ngOnInit(): Promise<void> {
    this.syncHeaderContext(this.router.url);

    const languageList = await lastValueFrom(this.translation.getLanguages(parseQueryParams(
      {
        pageIndex: 1,
        pageSize: 100,
        sortField: 'createdAt',
        sortOrder: 'ascend',
        where: { active: true },
        searchOption: 'EXACT',
      },
      'ASC',
    )));
    this.languages = languageList;
    const codes = languageList.map((l) => l.languageISO2);
    if (codes.length) {
      this.translation.translate.addLangs(codes);
    }
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.syncHeaderContext(event.urlAfterRedirects);
      });
  }

  private syncHeaderContext(url: string): void {
    this.isMenuOpen = url.includes('/menu');
    this.isSimulationPage = url.split('?')[0].split('#')[0].includes('/simulation');
    // this.isSimulationPage = url.split('?')[0].split('#')[0].includes('/simulation') && (url.includes('?or=r') || url.includes('&or=r'));
    if (this.shouldPreserveApplicationContext(url)) {
      return;
    }
    this.wizardBrandingService.setActiveBrandingFromUrl(url);
    this.loadApplicationNameForUrl(url);
  }

  private shouldPreserveApplicationContext(url: string): boolean {
    const path = url.split('?')[0].split('#')[0];
    return path === '/menu';
  }

  private loadApplicationNameForUrl(url: string): void {
    const applicationId = this.extractApplicationIdFromUrl(url);
    if (!applicationId) {
      this.appName = '';
      return;
    }

    this.applicationService.setApplicationId(applicationId);
    this.applicationService.getApplicationById(applicationId).subscribe({
      next: (response: any) => {
        const resolvedApplicationScheme =
          response?.data?.application_type ??
          response?.application_type ??
          response?.data?.applicationType ??
          response?.applicationType ??
          null;
        const resolvedName =
          response?.data?.name ??
          response?.name ??
          response?.data?.application_name ??
          response?.application_name ??
          '';
        this.wizardBrandingService.setActiveBrandingFromApplicationScheme(resolvedApplicationScheme);
        if (typeof resolvedName === 'string' && resolvedName.trim()) {
          this.appName = resolvedName.trim();
          console.log('toto', this.appName)
        }
      },
      error: () => {
        this.appName = '';
      },
    });
  }

  private extractApplicationIdFromUrl(url: string): number | null {
    const path = url.split('?')[0];
    const segments = path.split('/').filter(Boolean);

    let rawApplicationId: string | undefined;

    if (segments[0] === 'application' || segments[0] === 'cncs-wizard' || segments[0] === 'simulation') {
      rawApplicationId = segments[1];
    } else if (segments[1] === 'application') {
      rawApplicationId = segments[2];
    }

    if (!rawApplicationId) {
      return null;
    }

    const applicationId = Number(rawApplicationId);
    return Number.isInteger(applicationId) && applicationId > 0 ? applicationId : null;
  }

  openMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
    if (this.isMenuOpen) {
      this.router.navigate(['/menu']);
    } else {
      this.location.back();
    }
  }

  ngAfterViewInit(): void {
    this.updateTopBarHeightCssVar();
    const el = this.elRef.nativeElement;
    if (el && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => this.updateTopBarHeightCssVar());
      ro.observe(el);
      this.destroyRef.onDestroy(() => ro.disconnect());
    }
  }

  private updateTopBarHeightCssVar(): void {
    const el = this.elRef.nativeElement;
    const height =
      el?.offsetHeight ?? el?.getBoundingClientRect?.()?.height ?? this.defaultTopBarHeightPx;
    const px = `${Math.round(height)}px`;
    document.documentElement.style.setProperty('--app-top-bar-height', px);
  }

  openDashboardWeb(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const dashboardUrl = this.authService.getDashboardAutoLoginUrl(
      '/dashboard',
      true,
      this.appName
    );

    if (!dashboardUrl) {
      this.router.navigate(['/login']);
      return;
    }

    window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
  }

  openManagerApplications(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    const reference = (this.appName || '').trim();
    const redirectPath = reference
      ? `/applications?lead-status=${encodeURIComponent(reference)}`
      : '/applications';
    const url = this.authService.getDashboardAutoLoginUrl(redirectPath, false);

    if (!url) {
      this.router.navigate(['/login']);
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  }
}
